// src/core/query/useQueryRunner.ts
import { useState, useCallback, useRef, useEffect } from "react";
import type {
  AsyncDuckDBConnection,
  AsyncDuckDBPreparedStatement,
} from "@duckdb/duckdb-wasm";
import { arrowTableToObjects, stripLimit, getLimit } from "@/lib";

export interface QueryResult {
  data: any[];
  cols: string[];
  rowCount: number;
  totalCount: number;
  isPaginated: boolean;
  durationMs: number;
  error: Error | null;
}

/**
 * High-level query helper with automatic COUNT() + paged fetching via prepared statements.
 */
export function useQueryRunner(
  conn: AsyncDuckDBConnection | null,
  maxRows: number | null = 1000,
  pageSize = 100
) {
  // ── Refs ────────────────────────────────────────────────────────────
  const baseSqlRef = useRef<string>("");           // SELECT ... minus any outer LIMIT
  const userLimitRef = useRef<number | null>(null); // user-supplied LIMIT n

  // Prepared statements (we’ll close() these when done)
  const countStmtRef = useRef<AsyncDuckDBPreparedStatement | null>(null);
  const pageStmtRef  = useRef<AsyncDuckDBPreparedStatement | null>(null);

  // ── React state ───────────────────────────────────────────────────
  const [page, setPage] = useState(0);
  const [result, setResult] = useState<QueryResult>({
    data: [],
    cols: [],
    rowCount: 0,
    totalCount: 0,
    isPaginated: false,
    durationMs: 0,
    error: null,
  });

  // ── Helpers ────────────────────────────────────────────────────────
  const finalizeStmts = () => {
    countStmtRef.current?.close?.();
    pageStmtRef.current?.close?.();
    countStmtRef.current = null;
    pageStmtRef.current  = null;
  };

  // ── run(): prepare + execute a brand-new SQL ─────────────────────────
  const run = useCallback(
    async (sqlText: string) => {
      if (!conn) return;

      // reset state & dispose old statements
      setPage(0);
      setResult((r) => ({ ...r, error: null }));
      finalizeStmts();

      const t0 = performance.now();
      const trimmed = sqlText.trim().replace(/;$/, "");

      // 1️⃣ Non-SELECT → just run normally
      if (!/^\s*select\b/i.test(trimmed)) {
        try {
          const rawRes = await conn.query(trimmed);
          const rows   = arrowTableToObjects(rawRes);
          const cols   = rows.length ? Object.keys(rows[0]) : [];
          setResult({
            data: rows,
            cols,
            rowCount: rows.length,
            totalCount: rows.length,
            isPaginated: false,
            durationMs: performance.now() - t0,
            error: null,
          });
        } catch (e: any) {
          setResult((r) => ({ ...r, error: e }));
        }
        return;
      }

      // 2️⃣ SELECT → strip outer LIMIT, prepare COUNT & paged SELECT
      try {
        // a) strip / detect LIMIT
        const raw      = trimmed;
        const uLimit   = getLimit(raw);
        const stripped = stripLimit(raw);
        baseSqlRef.current   = stripped;
        userLimitRef.current = uLimit;

        // b) prepare and execute COUNT(*)
        const countSql = `SELECT COUNT(*) AS c FROM (${stripped})`;
        countStmtRef.current = await conn.prepare(countSql);
        const cntRows = arrowTableToObjects(
          await countStmtRef.current.query()
        );
        let total = Number(cntRows[0]?.c || 0);
        if (uLimit != null && uLimit < total) total = uLimit;
        if (maxRows != null && total > maxRows) total = maxRows;

        // c) build inner query enforcing any user-LIMIT
        const innerSql = uLimit != null
          ? `${stripped} LIMIT ${uLimit}`
          : stripped;

        // d) prepare paginated statement with placeholders
        const pagedSql = `SELECT * FROM (${innerSql}) LIMIT ? OFFSET ?`;
        pageStmtRef.current = await conn.prepare(pagedSql);

        // e) fetch first page
        const firstRows = arrowTableToObjects(
          await pageStmtRef.current.query(pageSize, 0)
        );
        const firstCols = firstRows.length ? Object.keys(firstRows[0]) : [];

        setResult({
          data: firstRows,
          cols: firstCols,
          rowCount: firstRows.length,
          totalCount: total,
          isPaginated: total > pageSize,
          durationMs: performance.now() - t0,
          error: null,
        });
      } catch (e: any) {
        setResult((r) => ({ ...r, error: e }));
      }
    },
    [conn, maxRows, pageSize]
  );

  // ── loadPage(): reuse prepared SELECT for LIMIT/OFFSET ───────────────
  const loadPage = useCallback(
    async (newPage: number) => {
      if (!conn || !result.isPaginated || !pageStmtRef.current) return;
      const offset = newPage * pageSize;
      try {
        const rows = arrowTableToObjects(
          await pageStmtRef.current.query(pageSize, offset)
        );
        setResult((r) => ({
          ...r,
          data: rows,
          rowCount: rows.length,
        }));
        setPage(newPage);
      } catch (e: any) {
        setResult((r) => ({ ...r, error: e }));
      }
    },
    [conn, pageSize, result.isPaginated]
  );

  // ── clean up when unmounting ────────────────────────────────────────
  useEffect(() => finalizeStmts, []);

  // ── public API ─────────────────────────────────────────────────────
  return {
    ...result,
    page,
    run,
    loadPage,
    next: () =>
      loadPage(
        Math.min(page + 1, Math.ceil(result.totalCount / pageSize) - 1)
      ),
    prev: () => loadPage(Math.max(page - 1, 0)),
  };
}
